import { createEmptyCounts } from '@/config/vehicleTypes';
import type { CountEvent, IntervalCounts, VehicleCounts } from '@/types/survey';
import { pad2 } from '@/utils/format';

/**
 * Clock-aligned 15 minute bucketing.
 *
 * Periods align to the wall clock — :00, :15, :30, :45 — not to when recording
 * started. A survey begun at 07:37 therefore reports 07:30–07:45 (a partial
 * period), then 07:45–08:00, and so on, which is what makes counts from
 * different surveyors on different roads directly comparable.
 *
 * Bucketing uses local time deliberately: the surveyor's wall clock is the
 * unit of analysis, and Sri Lanka's +05:30 offset means UTC-based flooring
 * would land on :00/:15/:30/:45 shifted by 30 minutes.
 */

export const INTERVAL_MINUTES = 15;
export const INTERVAL_MS = INTERVAL_MINUTES * 60 * 1000;

/**
 * The start of the 15 minute period containing `timestamp`, in local time.
 *
 * Uses calendar fields rather than modular arithmetic on the epoch, so it stays
 * correct across a daylight-saving shift or any non-hour timezone offset.
 */
export function intervalStart(timestamp: number): number {
  const d = new Date(timestamp);
  d.setMinutes(Math.floor(d.getMinutes() / INTERVAL_MINUTES) * INTERVAL_MINUTES, 0, 0);
  return d.getTime();
}

/** The start of the period after the one containing `timestamp`. */
export function nextIntervalStart(timestamp: number): number {
  const d = new Date(intervalStart(timestamp));
  d.setMinutes(d.getMinutes() + INTERVAL_MINUTES);
  return d.getTime();
}

/** "07:30 – 07:45" */
export function formatIntervalRange(start: number, end: number): string {
  const s = new Date(start);
  const e = new Date(end);
  return `${pad2(s.getHours())}:${pad2(s.getMinutes())} – ${pad2(e.getHours())}:${pad2(e.getMinutes())}`;
}

/** "07:30" — the period label used in compact tables and the TXT export. */
export function formatIntervalLabel(start: number): string {
  const s = new Date(start);
  return `${pad2(s.getHours())}:${pad2(s.getMinutes())}`;
}

/**
 * Groups count events into clock-aligned periods.
 *
 * Periods with no vehicles at all are still emitted when they fall between the
 * first and last period that do have activity — a quiet 15 minutes is a real
 * observation, and silently dropping it would misrepresent the survey.
 * Set `from`/`to` to anchor the range to the actual recording window so a
 * leading or trailing quiet period is not lost either.
 */
export function buildIntervals(events: CountEvent[], from?: number, to?: number): IntervalCounts[] {
  const buckets = new Map<number, VehicleCounts>();

  const ensure = (start: number): VehicleCounts => {
    let counts = buckets.get(start);
    if (!counts) {
      counts = createEmptyCounts();
      buckets.set(start, counts);
    }
    return counts;
  };

  for (const event of events) {
    const counts = ensure(intervalStart(event.at));
    counts[event.vehicleTypeId] = Math.max(0, (counts[event.vehicleTypeId] ?? 0) + event.delta);
  }

  if (buckets.size === 0 && from === undefined) return [];

  // Anchor the range to the recording window when we know it.
  const starts = [...buckets.keys()];
  if (from !== undefined) starts.push(intervalStart(from));
  if (to !== undefined) starts.push(intervalStart(to));

  const first = Math.min(...starts);
  const last = Math.max(...starts);

  const result: IntervalCounts[] = [];
  // Step with the calendar rather than by adding INTERVAL_MS, so a DST change
  // inside a long survey cannot drift the boundaries.
  for (let start = first; start <= last; start = nextIntervalStart(start)) {
    const counts = buckets.get(start) ?? createEmptyCounts();
    let total = 0;
    for (const value of Object.values(counts)) total += value;
    result.push({ start, end: nextIntervalStart(start), counts, total });
  }

  return result;
}

/** Sum across every period — must equal the survey total. */
export function sumIntervals(intervals: IntervalCounts[]): VehicleCounts {
  const totals = createEmptyCounts();
  for (const interval of intervals) {
    for (const [id, value] of Object.entries(interval.counts)) {
      totals[id] = (totals[id] ?? 0) + value;
    }
  }
  return totals;
}

/** Periods that actually recorded something, for a compact display. */
export function nonEmptyIntervals(intervals: IntervalCounts[]): IntervalCounts[] {
  return intervals.filter((interval) => interval.total > 0);
}
